import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { useLocale } from "@/i18n/LocaleProvider";
import { goToLoginAndReturn } from "@/auth/returnTo";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { useFeaturedListings, useMarketData, usePlans } from "@/api/hooks";
import { orderForRail, type Listing } from "@/api/listings";
import { formatNumber } from "@/lib/format";
import { useMarket } from "../MarketProvider";
import { LIST_PROPERTY_PATH, useMarketLink } from "../links";
import { useIsMobile, useV3 } from "../useV3";
import { SearchBar } from "../SearchBar";
import { ListingCardSkeleton, ListingCardV3 } from "../ListingCard";
import { currencyPerMonth } from "../money";
import { Alert, Send } from "../Icons";

import { isTypeIcon, typeIconUrl } from "@/v3/typeIcons";
import { BackdropImage } from "@/v3/BackdropImage";
import { fetchTypes, type SiteType } from "@/api/cms";
import { SEED_TYPES } from "@/v3/propertyTypes";

/* The homepage, built from docs/design/v3/home-{ar,en}-{1440,390}.html.
 *
 * WHICH SECTIONS RENDER, AND IN WHAT ORDER, IS DATA. `home.sections` is a
 * site_settings row -- an admin can hide the advisor band or move plans above
 * featured without a deploy. The fallback order is the design's.
 *
 * WHAT IS NOT DRAWN, and why, each logged in docs/DEVIATIONS.md:
 *   hero photo      no image has been chosen. The design's own .sq-hero
 *                   background under the photo is the navy the scrim sits on,
 *                   so the frameless hero is the design's, not a substitute.
 *   area photos     there is no area-image source. The tile's own navy
 *                   background carries the name, which the design already
 *                   draws for a slow image.
 *   projects tab    no projects backend.
 *   8 type tiles    the design draws eight; the backend knows five, and the
 *                   design's own rule is `propertyType.hasListings`.
 *   plan "featured  no such column on `plans`.
 *   limit", gold    no "recommended" flag on `plans` either, so no card is
 *   recommended     singled out. Both are backend gaps, not design choices.
 */

type SectionKey = "hero" | "types" | "areas" | "featured" | "advisor" | "listerBand" | "plans";

export function HomeV3() {
  const { s, locale, ar, dir } = useV3();
  const mobile = useIsMobile();
  const { market, settings } = useMarket();
  const link = useMarketLink();
  const { profile } = useAuth();
  const navigate = useNavigate();
  useDocumentMeta((d) => ({ title: d.meta.documentTitle, description: d.meta.descHome }));

  const geo = useMarketData();
  /* 24 fetched, up to `home.featured.count` shown. The pool is bigger than the
     grid because the rail is ranked client-side by tag, and a page of 8 could
     hold no Featured listing while several sit just below it. */
  /* Scoped to the market being rendered: /sa/ must never show Egypt's flats. */
  const listings = useFeaturedListings(24, market?.id ?? null);
  const plans = usePlans();

  const onFixtures = geo.source === "fixture" || listings.source === "fixture";

  const featured = useMemo(() => {
    const count = Math.max(1, settings.featured.count || 8);
    /* Featured > Hot > New > untagged -- Bassem's ruling, implemented in
       orderForRail so the rail and the API agree about what "featured" means.
       Never padded and never repeated: six matching listings show six. */
    return orderForRail(listings.data ?? []).slice(0, count);
  }, [listings.data, settings.featured.count]);

  /* Types: Admin's list, in Admin's order.
   *
   * `/public-types` arrived in proxy v3.8.6. Until that is deployed this
   * resolves to null and the fallback below applies -- the standard seed set
   * narrowed to the types that ACTUALLY HAVE A LISTING here, which is what
   * this page derived for itself before the types table existed. Every tile
   * in that answer still leads somewhere real; what is missing until the
   * deploy is Bassem's order and his per-market switches.
   *
   * `mode` is his home switch (migration 007): every enabled type, or only
   * the ones with a live listing. */
  const [apiTypes, setApiTypes] = useState<SiteType[] | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    let live = true;
    void fetchTypes(market?.slug ?? null, settings.typesMode === "withListings", ctrl.signal)
      .then((r) => { if (live) setApiTypes(r); });
    return () => { live = false; ctrl.abort(); };
  }, [market?.slug, settings.typesMode]);

  const types = useMemo<SiteType[]>(() => {
    if (apiTypes?.length) return apiTypes;
    /* THE FALLBACK. Real types only: the seed list, kept to the ones a
       listing in this market actually uses. */
    const live = new Set((listings.data ?? []).map((l) => l.type).filter(Boolean));
    return SEED_TYPES
      .filter((t) => t.legacy && live.has(t.legacy))
      .map((t) => ({
        id: null, slug: t.slug, legacy: t.legacy,
        nameAr: t.nameAr, nameEn: t.nameEn, icon: t.icon,
        category: t.category, fields: [...t.fields],
        listings: 0, activeListings: 0,
      }));
  }, [apiTypes, listings.data]);

  /* HOW MANY FIT ON THE HOME ROW is a setting, not a property of the list.
     When more types exist than fit, the LAST tile becomes the way to the rest
     -- so the row stays the width the design drew and nothing is silently
     dropped. Disabling a type removes it from the site entirely; this only
     decides how many reach the front page. */
  const typeHref = (ty: SiteType) => link(typeHrefFor(ty));
  const typeCount = Math.max(1, Number(settings.typeCount) || 8);
  const typeOverflow = types.length > typeCount;
  const shownTypes = typeOverflow ? types.slice(0, typeCount - 1) : types.slice(0, typeCount);

  /* Areas: the districts that actually carry a listing. The LABEL comes from
     the districts table (name_ar / name_en), not listings.district_text,
     which is whatever the lister typed and is English on most rows. */
  /* Areas: the districts that actually carry a listing, arranged the way
     Bassem arranged them.

     THE BASE IS STILL THE DISTRICTS WITH LISTINGS. A tile that opens an empty
     results page is worse than no tile, and that has not changed; what his
     settings decide is the ORDER, which of them show, and how many.

     An area in neither `order` nor `hidden` keeps its natural position and is
     visible — a new district must not need a settings edit before it can
     appear, and must not vanish because nobody has ranked it yet. */
  const areas = useMemo(() => {
    const byId = new Map((geo.data?.districts ?? []).map((d) => [d.id, d]));
    const cfg = settings.areas;

    /* THE LABEL A TILE SHOWS. A district may carry a SHORT name for places
     * where the full one will not fit — «التجمع الخامس» where «القاهرة
     * الجديدة - التجمع الخامس» wraps to two lines. It is editorial, set in
     * Admin, and null almost everywhere, so the full name is the normal case. */
    const labelOf = (d: { nameAr: string; nameEn: string; shortNameAr: string; shortNameEn: string }) =>
      (ar ? (d.shortNameAr || d.nameAr) : (d.shortNameEn || d.nameEn));

    const seen = new Map<string, { id: number | null; label: string }>();

    /* WHERE THE AREAS COME FROM, and the reversal in it.
     *
     * This used to iterate the LISTINGS and take their districts, so an area
     * with nothing listed in it could not appear at all. That silently hid
     * الرحاب, مدينتي and التجمع الأول — three of the first four areas Bassem
     * had ordered, each with a photograph he had uploaded — and it looked like
     * a broken image pipeline rather than a rule.
     *
     * Now the base is the DISTRICTS, and `requireListings` (Admin, per market,
     * default off) decides whether an empty one is allowed through. A district
     * only qualifies if it has a photograph: a tile is a picture with a name
     * on it, and an area with neither listings nor a picture has nothing to
     * show. */
    const photoOf = (id: number | null) =>
      id != null ? settings.areaImages[String(id)] ?? null : null;

    if (!cfg.requireListings) {
      for (const d of geo.data?.districts ?? []) {
        if (!photoOf(d.id)) continue;
        const label = labelOf(d);
        if (!label || seen.has(label)) continue;
        seen.set(label, { id: d.id, label });
      }
    }

    /* Districts that DO carry a listing, always — including the free-text ones
     * a lister typed, which have no district row to read a photograph from. */
    for (const l of listings.data ?? []) {
      const known = l.districtId != null ? byId.get(l.districtId) : undefined;
      const label = known ? labelOf(known) : l.districtText;
      if (!label || seen.has(label)) continue;
      seen.set(label, { id: l.districtId, label });
    }

    const hidden = new Set(cfg.hidden);
    const rank = new Map(cfg.order.map((id, i) => [id, i]));

    let list = [...seen.values()]
      /* An area with no district id came from free text a lister typed. It
         cannot be ranked, hidden or given a photograph, because there is
         nothing stable to key any of that on. It keeps its place at the end. */
      .filter((a) => a.id == null || !hidden.has(a.id));

    if (cfg.photosOnly) list = list.filter((a) => !!photoOf(a.id));

    const natural = new Map(list.map((a, i) => [a.label, i]));
    const position = (a: { id: number | null; label: string }) =>
      (a.id != null && rank.has(a.id))
        ? rank.get(a.id)!
        /* Unranked areas sort after every ranked one, keeping their own
           relative order rather than jumping to the front. */
        : cfg.order.length + (natural.get(a.label) ?? 0);

    list = [...list].sort((x, y) => {
      if (cfg.photosFirst) {
        const px = photoOf(x.id) ? 0 : 1;
        const py = photoOf(y.id) ? 0 : 1;
        if (px !== py) return px - py;
      }
      return position(x) - position(y);
    });

    return list;
  }, [listings.data, geo.data, ar, settings.areas, settings.areaImages]);

  /* Same shape as the type row: when more areas are visible than fit, the LAST
     tile becomes the way to the rest rather than the rest being dropped. */
  const areaCount = settings.areas.count;
  const areaOverflow = areas.length > areaCount;
  const shownAreas = areaOverflow ? areas.slice(0, areaCount - 1) : areas.slice(0, areaCount);

  const goList = () => {
    const target = link(LIST_PROPERTY_PATH);
    if (profile) { navigate(target); return; }
    goToLoginAndReturn(navigate, target);
  };

  const advisorHref = profile ? link("/account#ai") : link("/login");
  const heroTitle = settings.hero.title[locale] || s.hero.title;
  const heroSubtitle = settings.hero.subtitle[locale] || s.hero.subtitle;

  const sections: Record<SectionKey, React.ReactNode> = {
    hero: (
      <section key="hero" data-section="hero" className="sq-hero">
        {/* The picture, then the design's scrim over it. The scrim is drawn
            whether or not there is a photograph, because the headline has to
            pass AA on a picture nobody has seen yet -- see BackdropImage. */}
        <BackdropImage image={settings.hero.image} className="sq-hero__img"
          sizes="100vw" priority scrim="hero" />
        <div className="sq-hero__scrim" aria-hidden="true" />
        <div className="sq-hero__content">
          <h1 className="sq-hero__title">{heroTitle}</h1>
          <p className="sq-hero__subtitle">{heroSubtitle}</p>
          <SearchBar market={geo.data} />
        </div>
      </section>
    ),

    types: shownTypes.length > 0 ? (
      <section key="types" data-section="browse-types"
        className={mobile ? "sq-section" : "sq-section sq-gutter"}>
        <div className="sq-section__head" style={mobile ? { paddingInline: 16 } : undefined}>
          <h2 className="sq-section__title">{s.sections.browseTypes}</h2>
        </div>
        {mobile ? (
          /* Bassem's ruling 2026-09-26: four across, two rows, rather than
             the package's horizontally scrolling chip row -- with eight tiles
             a phone should see all of them without dragging. Logged in
             docs/DEVIATIONS.md. */
          /* The package's own scrolling chip row, restored. A previous round
             made this four-across in two rows on a ruling; that ruling was
             withdrawn 2026-09-26 and the design stands. */
          <div className="sq-chip-row" style={{ paddingInline: 16 }}>
            {shownTypes.map((ty) => (
              <Link key={ty.slug} to={typeHref(ty)}
                data-component="type-tile" className="sq-chip">
                <TypeTileIcon icon={ty.icon} size={22} />
                <span>{ar ? ty.nameAr : ty.nameEn}</span>
              </Link>
            ))}
            {typeOverflow && (
              <Link to={link("/search")} data-component="type-tile-all"
                className="sq-chip sq-chip--all">
                <span>{s.sections.allTypes}</span>
              </Link>
            )}
          </div>
        ) : (
          /* Eight columns, whatever the count. The design's tile is sized by
             the column, so stretching three tiles across the full width to
             avoid empty columns would make them three times the drawn size --
             a bigger difference from the design than the gap it removed. */
          <div className="sq-grid-8">
            {shownTypes.map((ty) => (
              <Link key={ty.slug} to={typeHref(ty)}
                data-component="type-tile" className="sq-type-tile">
                <TypeTileIcon icon={ty.icon} size={40} />
                <span>{ar ? ty.nameAr : ty.nameEn}</span>
              </Link>
            ))}
            {typeOverflow && (
              <Link to={link("/search")} data-component="type-tile-all"
                className="sq-type-tile sq-type-tile--all">
                <span>{s.sections.allTypes}</span>
              </Link>
            )}
          </div>
        )}
      </section>
    ) : null,

    areas: shownAreas.length > 0 ? (
      <section key="areas" data-section="browse-areas" className="sq-section sq-gutter"
        style={{ paddingTop: mobile ? 24 : 48 }}>
        <div className="sq-section__head">
          <h2 className="sq-section__title">{s.sections.browseAreas}</h2>
        </div>
        <div className={mobile ? "sq-grid-2" : "sq-grid-4 sq-grid-4--tight"}>
          {shownAreas.map((a) => (
            <Link key={a.label} data-component="area-tile" className="sq-area-tile"
              to={a.id != null
                ? link(`/search?district=${a.id}`)
                : link(`/search?q=${encodeURIComponent(a.label)}`)}>
              {/* With no uploaded picture this renders nothing and the tile
                  keeps the design's own navy — which IS the package's no-image
                  treatment, not a placeholder for one. */}
              <BackdropImage image={a.id != null ? settings.areaImages[String(a.id)] : null}
                className="sq-area-tile__img"
                sizes={mobile ? "50vw" : "25vw"} scrim="area" />
              <span className="sq-area-tile__name">{a.label}</span>
            </Link>
          ))}
          {areaOverflow && (
            <Link to={link("/search")} data-component="area-tile-all"
              className="sq-area-tile sq-area-tile--all">
              <span className="sq-area-tile__name">{s.sections.allAreas}</span>
            </Link>
          )}
        </div>
      </section>
    ) : null,

    featured: (
      <section key="featured" data-section="featured-listings" className="sq-section sq-gutter">
        <div className="sq-section__head">
          <h2 className="sq-section__title">{s.sections.featured}</h2>
          <Link to={link("/search")} className="sq-link">
            {mobile ? s.sections.allListingsShort : s.sections.allListings}
          </Link>
        </div>
        {listings.loading ? (
          <div className={mobile ? "sq-stack" : "sq-grid-4"} aria-busy="true"
            aria-label={s.featuredStates.loading}>
            {[0, 1, 2, 3].map((i) => <ListingCardSkeleton key={i} />)}
          </div>
        ) : listings.failure && !listings.data?.length ? (
          /* Inline, and the page stays usable -- the design's error state is a
             banner inside the section, not a screen that replaces it. */
          <div className="sq-banner-error" role="alert">
            <Alert />
            <span>{s.featuredStates.error}</span>
            <button type="button" className="sq-btn sq-btn--outline sq-btn--sm"
              style={{ borderColor: "var(--sq-color-error)", color: "var(--sq-color-error)" }}
              onClick={listings.reload}>
              {s.featuredStates.retry}
            </button>
          </div>
        ) : featured.length === 0 ? (
          <div className="sq-empty">
            <div className="sq-empty__title">{s.featuredStates.emptyTitle}</div>
            <p>{s.featuredStates.emptyBody}</p>
            <button type="button" className="sq-btn sq-btn--primary" onClick={goList}>
              {s.featuredStates.emptyCta}
            </button>
          </div>
        ) : (
          <div className={mobile ? "sq-stack" : "sq-grid-4"}>
            {featured.map((l: Listing) => (
              <ListingCardV3 key={l.id} listing={l} geo={geo.data} />
            ))}
          </div>
        )}
      </section>
    ),

    /* The advisor band renders only where the market has the feature. Saudi's
       row can turn it off, and a band inviting you to try something this
       market does not have is an advertisement for a 404. */
    advisor: market?.features.aiAdvisor !== false ? (
      /* MOBILE IS A DIFFERENT BAND, not the same one narrower: the 390 file
         writes a shorter body, drops the third bubble and the composer, and
         moves the CTA below the sample as a full-width button. All of that is
         the design's, not a simplification made here. */
      <section key="advisor" data-section="ai-advisor" className="sq-advisor">
        <div>
          <span className="sq-badge sq-badge--outline" style={{ marginBottom: mobile ? 10 : 16 }}>
            {s.advisor.badge}
          </span>
          <h2 className="sq-advisor__title">{s.advisor.title}</h2>
          <p className="sq-advisor__body">{mobile ? s.advisor.bodyShort : s.advisor.body}</p>
          {!mobile && (
            <Link to={advisorHref} className="sq-btn sq-btn--navy">{s.advisor.cta}</Link>
          )}
        </div>
        {/* Labelled «مثال توضيحي» in the design and it stays labelled: a
            reader must not take a sample exchange for advice they were given. */}
        <div data-component="chat-sample" className="sq-chat-sample" aria-label={s.advisor.sampleLabel}>
          <span className="sq-chat-sample__label">{s.advisor.sampleLabel}</span>
          <div className="sq-bubble sq-bubble--user">{s.advisor.q1}</div>
          <div className="sq-bubble sq-bubble--ai">{s.advisor.a1}</div>
          {!mobile && <div className="sq-bubble sq-bubble--user">{s.advisor.q2}</div>}
          {/* The composer takes you to the assistant with what you typed; it
              does not answer here, because the advisor lives in the panel. */}
          {!mobile && (
            <form className="sq-composer" onSubmit={(e) => {
              e.preventDefault();
              const q = new FormData(e.currentTarget).get("ask");
              const ask = typeof q === "string" && q.trim() ? `?ask=${encodeURIComponent(q.trim())}` : "";
              navigate(profile ? link(`/account${ask}#ai`) : link("/login"));
            }}>
              <input className="sq-input" name="ask"
                placeholder={s.advisor.composerPlaceholder} aria-label={s.advisor.composerPlaceholder} />
              <button className="sq-btn sq-btn--primary sq-btn--icon" aria-label={s.advisor.send}>
                <Send rtl={dir === "rtl"} />
              </button>
            </form>
          )}
        </div>
        {mobile && (
          <Link to={advisorHref} className="sq-btn sq-btn--navy sq-btn--block">{s.advisor.cta}</Link>
        )}
      </section>
    ) : null,

    listerBand: (
      <section key="listerBand" data-section="lister-band" className="sq-band">
        <div>
          <h2 className="sq-band__title">
            {settings.listerBand.title[locale] || s.listerBand.title}
          </h2>
          {/* An admin-set body has no mobile variant -- it is one value in
              one settings row, and shortening someone's copy for them would
              be editing it. Only the FALLBACK has the design's short form. */}
          <p className="sq-band__body">
            {settings.listerBand.body[locale]
              || (mobile ? s.listerBand.bodyShort : s.listerBand.body)}
          </p>
        </div>
        <button type="button" onClick={goList}
          className={mobile
            ? "sq-btn sq-btn--primary sq-btn--block sq-btn--xl"
            : "sq-btn sq-btn--primary sq-btn--xl"}>
          {settings.listerBand.cta[locale] || s.listerBand.cta}
        </button>
      </section>
    ),

    /* Hidden when the platform has no enabled plan: a section headed
       "Plans for listers" with no plan under it advertises an absence. An
       enabled plan brings it back by itself. */
    plans: (!plans.loading && !plans.failure && !(plans.data ?? []).some((p) => p.enabled)) ? null : (
      <section key="plans" data-section="plans" className="sq-section sq-gutter"
        style={{ paddingTop: mobile ? 32 : 64 }}>
        <h2 className="sq-section__title"
          style={{ textAlign: mobile ? "start" : "center", marginBottom: 6 }}>
          {s.sections.plans}
        </h2>
        <p className="sq-field__hint"
          style={{ textAlign: mobile ? "start" : "center", marginBottom: mobile ? 14 : 28 }}>
          {mobile ? s.sections.plansHintShort : s.sections.plansHint}
        </p>
        {plans.loading ? (
          <div className="sq-grid-4 sq-grid-4--tight" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="sq-skeleton" style={{ height: 220 }} />
            ))}
          </div>
        ) : plans.failure ? (
          /* A heading that says "Plans for listers" over nothing at all is
             worse than saying the load failed. Inline, with a retry, like the
             featured rail -- the rest of the page stays usable. */
          <div className="sq-banner-error" role="alert">
            <Alert />
            <span>{s.plan.error}</span>
            <button type="button" className="sq-btn sq-btn--outline sq-btn--sm"
              style={{ borderColor: "var(--sq-color-error)", color: "var(--sq-color-error)" }}
              onClick={plans.reload}>
              {s.featuredStates.retry}
            </button>
          </div>
        ) : (
          <div className={mobile ? "sq-chip-row" : "sq-grid-4 sq-grid-4--tight"}
            style={mobile ? { gap: 12 } : undefined}>
            {(plans.data ?? []).filter((p) => p.enabled).map((p) => {
              /* effectivePrice is what the member would actually pay -- the
                 promo engine's number, never one recomputed here. */
              const price = p.effectivePrice ?? p.price;
              const card = (
                <div data-component="plan-card" className="sq-plan-card">
                  <span className="sq-badge sq-badge--muted">{p.name}</span>
                  <div className="sq-plan-card__price">
                    {/* THE NUMBER IS BARE. The design puts «ج.م / شهر» in the
                        hint beside it, so writing the currency into the price
                        as well printed it twice. */}
                    <strong style={{ fontSize: 20 }}>
                      {price === 0 || price == null
                        ? s.plan.free
                        : formatNumber(price, locale)}
                    </strong>
                    {price !== 0 && price != null && (
                      <span className="sq-field__hint">{currencyPerMonth(market, locale, s)}</span>
                    )}
                  </div>
                  {/* The promo is why the price says what it says. Without it
                      a plan priced at 490 reads «مجانًا» beside one at 999 and
                      looks like a bug. The design has no slot for this because
                      its cards are placeholders; the message is the server's,
                      never composed here. */}
                  {p.promo?.message && price !== p.price && (
                    <span className="sq-field__hint">{p.promo.message}</span>
                  )}
                  {/* NOT <code>. The design wraps these in <code> because in
                      the design they are placeholder tokens -- "plan.listings"
                      -- and monospace says "this is a name, not a value". With
                      real data it is wrong twice: the values are numbers, and
                      «بلا حد» set in a Latin monospace loses its letter joins
                      and reads as four disconnected glyphs. */}
                  <ul className="sq-plan-card__features">
                    <li>{s.plan.listings} {p.maxProperties == null
                      ? s.plan.unlimited : formatNumber(p.maxProperties, locale)}</li>
                    {/* null is UNLIMITED here, exactly as it is for listings --
                        it was being read as "no such limit to show", which hid
                        the best thing about the top plan. */}
                    <li>{s.plan.aiCredits} {p.aiMonthlyLimit == null
                      ? s.plan.unlimited : formatNumber(p.aiMonthlyLimit, locale)}</li>
                  </ul>
                  <Link to={link("/pricing")} className="sq-btn sq-btn--outline sq-btn--sm">
                    {s.plan.choose}
                  </Link>
                </div>
              );
              return mobile
                ? <div key={p.id} style={{ flex: "none", width: 240 }}>{card}</div>
                : <div key={p.id} style={{ display: "contents" }}>{card}</div>;
            })}
          </div>
        )}
      </section>
    ),
  };

  const order = settings.sections.filter((x) => x.visible).map((x) => x.key as SectionKey);

  return (
    <>
      {onFixtures && <FixtureNote />}
      {order.map((key) => sections[key] ?? null)}
    </>
  );
}

/* The type icons are the design's own files, drawn in colour at 40px. A type
 * the package has no icon for renders without one rather than borrowing
 * another type's glyph -- two tiles with the same picture is a worse lie than
 * one tile with none. Studio is that case today; logged in DEVIATIONS.md. */
/* A tile links by the word `listings.type` actually stores, because that is
   what the results filter matches on. A type with no legacy value -- one
   Bassem created after the table existed -- links by its slug, which the
   filter accepts as a plain string. */
const typeHrefFor = (ty: { legacy: string | null; slug: string }) =>
  `/search?type=${encodeURIComponent(ty.legacy ?? ty.slug)}`;

/* THE ICON THE TYPE CARRIES, not one derived from its name.
 *
 * This used to look the name up in the seed map, which works only for the
 * fourteen standard types. A type Bassem CREATES in Admin picks its icon from
 * the design's set and stores it on the row -- and that row is exactly what
 * the seed map has never heard of, so the tile rendered with no picture. The
 * defect item 5 was about, reintroduced one layer up.
 *
 * `isTypeIcon` keeps it to files that exist (the list is generated from the
 * folder), so a stale or hand-edited value renders nothing rather than a
 * broken image. */
function TypeTileIcon({ icon, size }: { icon: string; size: number }) {
  if (!isTypeIcon(icon)) return null;
  return <img src={typeIconUrl(icon)} alt="" width={size} height={size} loading="lazy" />;
}

/* Not a design element: the standing rule that a page rendering sample data
 * says so. The wording is the one the rest of the site already uses. */
function FixtureNote() {
  const { t } = useLocale();
  return <div className="sq-fixture-note" role="status">{t.status.fixtureNotice}</div>;
}
